import { Card, colors, H2, Separator, Skeleton } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { PersonAdd } from '@fs/zion-icon'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useMembersContext } from '../../providers/MembersProvider'
import ContributorRow from './ContributorRow'
import InviteButton from '../../components/details/InviteButton'
import { useTreeGroupDetailsContext } from '../providers/TreeGroupDetailsProvider'
import { useUserIsDataAdmin } from '../../providers/DataAdminProvider'

export default function ContributorsContent(): JSX.Element {
  const [t] = useTranslation()
  const { treeDetails } = useGroupsContext()
  const { nonAdmins, admins, userIsAdmin } = useMembersContext()
  const { group } = useTreeGroupDetailsContext()
  const { userIsDataAdmin } = useUserIsDataAdmin()

  const owner = admins.find((admin): boolean => admin.memberCisId === treeDetails?.ownerCisId)
  const coAdmins = admins?.filter((contributor) => contributor.memberCisId !== treeDetails?.ownerCisId) ?? []
  return (
    <Card backgroundColor={colors.gray00} edges="none">
      <H2 size="H5">{t('group-management.manage-contributors.heading')}</H2>
      <Separator size="sm" />

      {/* Admin Section */}
      {owner && (
        <ContributorRow
          title={t('group-management.manage-contributors.owner-label')}
          members={[owner]}
          adminCount={admins?.length}
          hideAdminTag
          permissionsTitle={t('group-management.manage-contributors.owner-permissions-title')}
          permissionsParagraph={t('group-management.manage-contributors.owner-permissions-description')}
          group={group}
        />
      )}

      {/* Co-Admin Section */}
      {coAdmins.length > 0 && (
        <ContributorRow
          title={t('group-management.manage-contributors.co-owner-label')}
          members={coAdmins}
          adminCount={admins?.length}
          hideAdminTag
          permissionsTitle={t('group-management.manage-contributors.co-owner-permissions-title')}
          permissionsParagraph={t('group-management.manage-contributors.co-owner-permissions-description')}
          group={group}
        />
      )}

      {/* Contributor Section */}
      {nonAdmins.length > 0 && (
        <ContributorRow
          title={t('group-management.contributors.heading')}
          members={nonAdmins}
          adminCount={admins?.length}
          hideAdminTag
          permissionsTitle={t('group-management.manage-contributors.contributor-permissions-title')}
          permissionsParagraph={t('group-management.manage-contributors.contributor-permissions-description')}
          group={group}
        />
      )}

      {userIsAdmin && !userIsDataAdmin && (
        /* eslint-disable-next-line test-selectors/button -- NA */
        <InviteButton Icon={PersonAdd} asActionChip={false} emphasis="medium" group={group} />
      )}
    </Card>
  )
}

export function ContributorsContentSkeleton(): JSX.Element {
  return (
    <Card backgroundColor={colors.gray00} edges="none">
      <Skeleton.H3 growX />
      <Separator size="sm" />
      <Skeleton.H4 />
      <Card backgroundColor={colors.gray03} edges="none" margins="xxs">
        <Skeleton.Paragraph maxWidth={400} />
      </Card>
      <Separator size="sm" />
      <Skeleton.H4 />
      <Card backgroundColor={colors.gray03} edges="none" margins="xxs">
        <Skeleton.Paragraph maxWidth={400} />
      </Card>
      <Separator size="sm" />
      <Skeleton.H4 />
      <Card backgroundColor={colors.gray03} edges="none" margins="xxs">
        <Skeleton.Paragraph maxWidth={400} />
      </Card>

      <Separator size="sm" />
      {/* eslint-disable-next-line test-selectors/button -- NA */}
      <Skeleton.Button />
    </Card>
  )
}
